import React, { useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useUsageControl } from '../hooks/useUsageControl'
import { supabase } from '../lib/supabase'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { UsageWarningBanner } from '../components/UsageWarningBanner'
import { Upload, Image as ImageIcon, FileText, Copy, Download, Loader2, Check, AlertCircle } from 'lucide-react'

interface OCRResult {
  text: string
  confidence: number
  language: string
  pageCount: number
  wordCount: number
  characterCount: number
}

export function OCRTool() {
  const { user } = useAuth()
  const { canPerformAction, useCredits, stats } = useUsageControl()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [result, setResult] = useState<OCRResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const OCR_CREDIT_COST = 1 // Cost per OCR operation

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setResult(null)
      setError(null)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setResult(null)
      setError(null)
    }
  }

  const processOCR = async () => {
    if (!selectedFile) return

    // Check credits before processing
    const creditCheck = await canPerformAction(OCR_CREDIT_COST)
    if (!creditCheck.allowed) {
      setError(creditCheck.reason || 'Insufficient credits')
      return
    }

    setProcessing(true)
    setError(null)

    try {
      // Convert file to base64
      const reader = new FileReader()
      reader.readAsDataURL(selectedFile)

      reader.onload = async () => {
        const base64Image = reader.result as string

        // Call Netlify function
        const response = await fetch('/.netlify/functions/ocr', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image: base64Image,
            mimeType: selectedFile.type,
          }),
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'OCR processing failed')
        }

        setResult(data)

        // Use credits and track activity
        const creditResult = await useCredits(
          OCR_CREDIT_COST,
          'ocr_processed',
          'ocr',
          {
            file_name: selectedFile.name,
            file_size: selectedFile.size,
            word_count: data.wordCount,
            confidence: data.confidence,
          }
        )

        if (!creditResult.success) {
          console.error('Failed to track credits:', creditResult.error)
        }
      }

      reader.onerror = () => {
        throw new Error('Failed to read file')
      }

    } catch (err: any) {
      setError(err.message || 'An error occurred during OCR processing')
    } finally {
      setProcessing(false)
    }
  }

  const copyToClipboard = async () => {
    if (result?.text) {
      await navigator.clipboard.writeText(result.text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const saveToLibrary = async () => {
    if (!result || !user) return

    setSaving(true)
    try {
      // Generate data URL for text download
      const textContent = result.text
      const dataUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(textContent)}`

      const { error } = await supabase.from('content_library').insert({
        user_id: user.id,
        tool_id: 'ocr',
        name: (selectedFile?.name.replace(/\.[^/.]+$/, '') || 'OCR Result') + '.txt',
        type: 'text',
        file_url: dataUrl, // Use data URL for download
        file_size: new Blob([result.text]).size,
        metadata: {
          content: result.text, // Store text content for preview
          confidence: result.confidence,
          language: result.language,
          word_count: result.wordCount,
          character_count: result.characterCount,
          original_file: selectedFile?.name,
        },
      })

      if (error) throw error

      alert('Saved to Content Library!')
    } catch (err: any) {
      setError(err.message || 'Failed to save to library')
    } finally {
      setSaving(false)
    }
  }

  const downloadAsText = () => {
    if (!result?.text) return

    const blob = new Blob([result.text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ocr-result-${Date.now()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">OCR Tool</h1>
        <p className="text-slate-600">
          Extract text from images and scanned documents with handwriting recognition
        </p>
      </div>

      {/* Usage Warning Banner */}
      <UsageWarningBanner />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Upload Image
          </h2>

          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">
              Click to upload or drag and drop
            </p>
            <p className="text-sm text-slate-400">
              PNG, JPG, GIF up to 10MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {previewUrl && (
            <div className="mt-4">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full rounded-lg border border-slate-200"
              />
              <Button
                onClick={processOCR}
                disabled={processing || stats?.isExceeded}
                className="w-full mt-4"
                title={stats?.isExceeded ? 'Credit limit exceeded - upgrade to continue' : ''}
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : stats?.isExceeded ? (
                  <>
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Credit Limit Reached
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Extract Text ({OCR_CREDIT_COST} credit)
                  </>
                )}
              </Button>
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Results Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Extracted Text
          </h2>

          {result ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Confidence</p>
                  <p className="text-lg font-semibold text-slate-800">
                    {result.confidence.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Words</p>
                  <p className="text-lg font-semibold text-slate-800">
                    {result.wordCount}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Language</p>
                  <p className="text-lg font-semibold text-slate-800 uppercase">
                    {result.language || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Extracted Text */}
              <div className="border border-slate-200 rounded-lg p-4 bg-white max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono">
                  {result.text}
                </pre>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="flex-1"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={downloadAsText}
                    variant="outline"
                    className="flex-1"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
                <Button
                  onClick={saveToLibrary}
                  disabled={saving}
                  className="w-full"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save to Library'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <FileText className="h-16 w-16 mb-4" />
              <p>Upload an image and click "Extract Text" to see results</p>
            </div>
          )}
        </Card>
      </div>

      {/* Tips */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-slate-800 mb-2">💡 Tips for Best Results</h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Use clear, high-resolution images for better accuracy</li>
          <li>• Ensure good lighting and contrast</li>
          <li>• The tool automatically handles skewed or rotated documents</li>
          <li>• Supports both printed text and handwriting</li>
          <li>• Works with 50+ languages</li>
        </ul>
      </Card>
    </div>
  )
}
